import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Pattern, renderPatternToSceneOptimized } from '@av-platform/rendering-core';

interface LayeredPatternsViewProps {
  patterns: Array<{ pattern: Pattern; color: string }>;
}

/**
 * Layered patterns view component
 * Renders multiple patterns vertically stacked in a rectangle
 * Each pattern takes a portion of the height
 * Canvas size matches pattern size (600x600)
 */
const LayeredPatternsView = ({ patterns }: LayeredPatternsViewProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const sceneRef = useRef<THREE.Scene | null>(null);
  const rendererRef = useRef<THREE.WebGLRenderer | null>(null);
  const cameraRef = useRef<THREE.OrthographicCamera | null>(null);
  const animationRef = useRef<number | null>(null);

  // Fixed pattern bounds - pattern is always 600x600 centered at origin
  const CLIP_BOUNDS = 300; // Half of 600
  const PATTERN_SIZE = CLIP_BOUNDS * 2; // 600
  const CANVAS_WIDTH = PATTERN_SIZE; // Canvas matches pattern size
  const CANVAS_HEIGHT = PATTERN_SIZE; // Canvas matches pattern size

  useEffect(() => {
    if (!containerRef.current || patterns.length === 0) return;

    const container = containerRef.current;
    const containerWidth = CANVAS_WIDTH;
    const containerHeight = CANVAS_HEIGHT;

    // Create scene
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xf5f5f5);
    sceneRef.current = scene;

    // Create camera - orthographic for 2D rendering
    const camera = new THREE.OrthographicCamera(
      -containerWidth / 2,
      containerWidth / 2,
      containerHeight / 2,
      -containerHeight / 2,
      0.1,
      2000
    );
    camera.position.set(0, 0, 100);
    camera.lookAt(0, 0, 0);
    cameraRef.current = camera;

    // Create renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(containerWidth, containerHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    container.innerHTML = '';

    const canvas = renderer.domElement;
    canvas.style.width = '100%';
    canvas.style.height = '100%';
    canvas.style.display = 'block';

    container.appendChild(canvas);
    rendererRef.current = renderer;

    // Calculate layer height
    const layerHeight = containerHeight / patterns.length;

    // Render each pattern layer
    patterns.forEach((layerData, index) => {
      const layerYBottom = -containerHeight / 2 + index * layerHeight;
      const layerYTop = layerYBottom + layerHeight;
      const layerYCenter = (layerYBottom + layerYTop) / 2;

      // Create a group for this layer
      const layerGroup = new THREE.Group();
      layerGroup.userData.layerId = index;
      layerGroup.userData.isLayer = true;
      layerGroup.position.y = layerYCenter;

      // Calculate scale to fit pattern into layer dimensions
      const patternScaleX = containerWidth / 600; // Scale to fit width
      const patternScaleY = layerHeight / 600; // Scale to fit height

      // Create temporary scene for rendering pattern
      const tempScene = new THREE.Scene();

      if (layerData.pattern.lines.length === 0) {
        // Solid fill
        const geo = new THREE.PlaneGeometry(containerWidth, layerHeight);
        const mat = new THREE.MeshBasicMaterial({
          color: layerData.color || '#000000',
        });
        const mesh = new THREE.Mesh(geo, mat);
        layerGroup.add(mesh);
      } else {
        renderPatternToSceneOptimized(tempScene, layerData.pattern, {
          patternScale: 1,
          color: layerData.color || '#000000',
          clipBounds: CLIP_BOUNDS,
          layerType: `layer-${index}`,
        });

        // Move and scale pattern objects to layer group
        tempScene.children.forEach((child) => {
          if (
            (child instanceof THREE.LineSegments || child instanceof THREE.Line) &&
            child.userData.layerType === `layer-${index}`
          ) {
            const cloned = child.clone();
            cloned.scale.set(patternScaleX, patternScaleY, 1);
            layerGroup.add(cloned);
          }
        });
      }

      // Cleanup tempScene
      tempScene.children.forEach((child) => {
        if (child instanceof THREE.LineSegments || child instanceof THREE.Line) {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach((mat) => mat?.dispose?.());
            } else {
              child.material.dispose();
            }
          }
        }
      });

      scene.add(layerGroup);
    });

    // Animation loop
    const animate = () => {
      animationRef.current = requestAnimationFrame(animate);
      renderer.render(scene, camera);
    };
    animate();

    // Handle resize
    const handleResize = () => {
      if (!container || !renderer || !camera || !scene) return;

      const newWidth = container.clientWidth;
      const newHeight = container.clientHeight;

      if (newWidth > 0 && newHeight > 0) {
        camera.left = -newWidth / 2;
        camera.right = newWidth / 2;
        camera.top = newHeight / 2;
        camera.bottom = -newHeight / 2;
        camera.updateProjectionMatrix();

        renderer.setSize(newWidth, newHeight);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
      resizeObserver.disconnect();
      if (container && renderer.domElement) {
        container.removeChild(renderer.domElement);
      }
      scene.children.forEach((child) => {
        if (child instanceof THREE.Group && child.userData.isLayer) {
          child.children.forEach((layerChild) => {
            if (layerChild instanceof THREE.LineSegments || layerChild instanceof THREE.Line) {
              if (layerChild.geometry) layerChild.geometry.dispose();
              if (layerChild.material) {
                if (Array.isArray(layerChild.material)) {
                  layerChild.material.forEach((mat) => mat?.dispose?.());
                } else {
                  layerChild.material.dispose();
                }
              }
            }
          });
        }
        scene.remove(child);
      });
      renderer.dispose();
    };
  }, [patterns, CLIP_BOUNDS, CANVAS_WIDTH, CANVAS_HEIGHT]);

  return (
    <div
      ref={containerRef}
      style={{
        width: `${CANVAS_WIDTH}px`,
        height: `${CANVAS_HEIGHT}px`,
        position: 'relative',
        overflow: 'hidden',
      }}
    />
  );
};

export default LayeredPatternsView;
